import { FC } from "react"
import { graphql, HeadFC } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
// ** Layout
import Layout from "../components/Layout"
import Box from "../components/Box"
import { serializers } from "../components/Serializers"
import Heading from "../components/Heading"
import PostAside from "../components/PostAside"

// ** Custom Components
import Container from "../components/Container"
import { SEO } from "../components/SEO"

// ** 3rd Party Library
import { PortableText } from "@portabletext/react"
import PageContext from "../contexts/PageContext"

const PostTemplate: FC<any> = ({ data }) => {
  const post = data?.sanityPost
  const title = post?.title
  const sections = post?.sections
  const contentSection = sections?.find(
    (section: any) => section?.label && section
  )
  const postHero = contentSection?.media?.image_desktop?.asset?.gatsbyImageData

  return (
    <PageContext.Provider value={post}>
      <Layout>
        <Box
          as="main"
          className="min-h-screen flex flex-col gap-12 lg:gap-16 pt-14 md:pt-24"
        >
          <Container
            className="flex flex-col md:flex-row gap-x-[30px] w-full"
            fullWidthOnMobile={false}
            constrainedToBreakPoint
          >
            <Box className="flex-1">
              {title && (
                <Heading
                  as="h2"
                  className="text-blue-500 w-full text-left mt-10 md:mt-14 mb-12 lg:mb-16"
                >
                  {title}
                </Heading>
              )}
              {postHero && (
                <GatsbyImage
                  image={postHero}
                  alt={title || "post Hero"}
                  objectFit="cover"
                  className="px-0 mb-8 shadow-lg rounded-xl overflow-hidden isolate"
                />
              )}

              <Box className="max-w-3xl">
                {contentSection._rawContent && (
                  <PortableText
                    value={contentSection._rawContent}
                    components={serializers}
                  />
                )}
              </Box>
            </Box>
            <PostAside title={title} />
          </Container>
        </Box>
      </Layout>
    </PageContext.Provider>
  )
}

export const postQuery = graphql`
  query ($slug: String!) {
    sanityPost(slug: { current: { eq: $slug } }) {
      title
      tags
      slug {
        current
      }
      seoTitle
      seoDescription
      i18n_lang
      i18n_refs {
        i18n_lang
        slug {
          current
        }
      }
      i18n_base {
        i18n_lang
        slug {
          current
        }
      }
      sections {
        ... on SanitySectionContent {
          id
          label
          _rawContent(resolveReferences: { maxDepth: 10 })
          media {
            image_desktop {
              asset {
                gatsbyImageData(
                  layout: CONSTRAINED
                  formats: [WEBP, AUTO]
                  width: 900
                  breakpoints: [400, 800]
                  placeholder: NONE
                )
              }
            }
            label
          }
        }
      }
    }
  }
`

export default PostTemplate

export const Head: HeadFC = ({ data }: any) => {
  const post = data.sanityPost
  return (
    <SEO title={post.seoTitle || ""} description={post.seoDescription || ""} />
  )
}
